class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // one debounced function instance
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);
  }

  connectedCallback() {
    // attach listeners here (safer for re-attachments)
    this.facetForm = this.querySelector('form');
    if (this.facetForm) {
      this.facetForm.addEventListener('input', this.debouncedOnSubmit);
    }

    this.facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (this.facetWrapper && typeof onKeyUpEscape === 'function') {
      this._onKeyUpEscape = (e) => onKeyUpEscape(e);
      this.facetWrapper.addEventListener('keyup', this._onKeyUpEscape);
    }
  }

  disconnectedCallback() {
    if (this.facetForm) {
      this.facetForm.removeEventListener('input', this.debouncedOnSubmit);
    }
    if (this.facetWrapper && this._onKeyUpEscape) {
      this.facetWrapper.removeEventListener('keyup', this._onKeyUpEscape);
    }
  }

  // --- static helpers & listeners ---
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static updateURLHash(searchParams) {
      const cleanPath = window.location.pathname.replace(/&page=\d+/, '') 

    history.pushState({ searchParams }, '', `${cleanPath}${searchParams && '?' + searchParams}`);
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    const id = grid?.dataset?.id;
    return id ? [{ section: id }] : [];
  }

  // Normalize & render
  static renderPage(searchParams, event, updateURLHash = true) {
    const normalized = new URLSearchParams(searchParams || '').toString();
    FacetFiltersForm.searchParamsPrev = normalized.replace(/&page=\d+/, '');

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const gridContainer = document.getElementById('ProductGridContainer');
      const cleanPath = window.location.pathname.replace(/&page=\d+/, '') 
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    gridContainer?.querySelector('.collection')?.classList.add('loading');
    countContainer?.classList.add('loading');
    countContainerDesktop?.classList.add('loading');

    sections.forEach((section) => {
      if (!section?.section) return;
      const url = `${cleanPath}?section_id=${section.section}&${normalized.replace(/&page=\d+/, '')}`;
      const cachedSection = FacetFiltersForm.filterData.find((entry) => entry.url === url);

      cachedSection
        ? FacetFiltersForm.renderSectionFromCache(cachedSection, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(normalized.replace(/&page=\d+/, ''));
  }

  static renderSectionFromFetch(url, event) {
    // cancel stale requests
    if (FacetFiltersForm._abortController) {
      try { FacetFiltersForm._abortController.abort(); } catch (e) {}
    }
    FacetFiltersForm._abortController = new AbortController();

    fetch(url, { signal: FacetFiltersForm._abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        FacetFiltersForm.cacheSectionResponse(url, responseText);
        const parsed = new DOMParser().parseFromString(responseText, 'text/html');
        FacetFiltersForm.renderFilters(parsed, event);
        FacetFiltersForm.renderProductGridContainer(parsed);
        FacetFiltersForm.renderProductCount(parsed);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        // hide spinners if error
        const loadingSpinners = document.querySelectorAll(
          '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
        document.getElementById('ProductCount')?.classList.remove('loading');
        document.getElementById('ProductCountDesktop')?.classList.remove('loading');
        document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading');        
        console.warn('Facet fetch failed:', err);
      });
  }

  static renderSectionFromCache(cached, event) {
    if (!cached?.html) return;
    const parsed = new DOMParser().parseFromString(cached.html, 'text/html');
    FacetFiltersForm.renderFilters(parsed, event);
    FacetFiltersForm.renderProductGridContainer(parsed);
    FacetFiltersForm.renderProductCount(parsed);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
  }

  static cacheSectionResponse(url, html) {
    const currentIndex = FacetFiltersForm.filterData.findIndex((entry) => entry.url === url);
    if (currentIndex !== -1) {
      FacetFiltersForm.filterData.splice(currentIndex, 1);
    }

    FacetFiltersForm.filterData.push({ url, html });

    // Keep cache bounded to avoid unbounded memory growth on many filter combinations.
    if (FacetFiltersForm.filterData.length > FacetFiltersForm.maxCachedResponses) {
      FacetFiltersForm.filterData.shift();
    }
  }

  static renderProductGridContainer(parsedDoc) {
    const dest = document.getElementById('ProductGridContainer');
    const src = parsedDoc.getElementById('ProductGridContainer');
    if (!dest || !src) return;

    dest.innerHTML = src.innerHTML;
    window.updateProductGrid()
    dest.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
    dest.querySelector('.collection')?.classList.remove('loading');
    
  }

  static renderProductCount(parsedDoc) {
    const incoming = parsedDoc.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (incoming && container) {
      container.innerHTML = incoming.innerHTML;
      container.classList.remove('loading');
    }
    if (incoming && containerDesktop) {
      containerDesktop.innerHTML = incoming.innerHTML;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(parsedDoc, event) {
    const fetched = parsedDoc.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetSortDrawerForm .js-filter'
    );
    const inDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetSortDrawerForm .js-filter'
    );

    // Remove facets that no longer exist
    Array.from(inDom).forEach((cur) => {
      if (!Array.from(fetched).some(({ id }) => cur.id === id)) cur.remove();
    });

    const matchesId = (el) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? el.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(fetched).filter((el) => !matchesId(el));
    const countsToRender = Array.from(fetched).find(matchesId);

    facetsToRender.forEach((elToRender, index) => {
      const current = document.getElementById(elToRender.id);
      if (current) {
        current.innerHTML = elToRender.innerHTML;
      } else {
        if (index > 0) {
          const prev = facetsToRender[index - 1];
          if (elToRender.className === prev.className) {
            document.getElementById(prev.id)?.after(elToRender);
            return;
          }
        }
        if (elToRender.parentElement) {
          const sibling = document.querySelector(`#${elToRender.parentElement.id} .js-filter`);
          sibling?.before(elToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedDoc);
    FacetFiltersForm.renderAdditionalElements(parsedDoc);

    if (countsToRender && event?.target) {
      const jsFilter = event.target.closest('.js-filter');
      const closestId = jsFilter?.id;
      if (closestId) {
        FacetFiltersForm.renderCounts(countsToRender, jsFilter);
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestId));
        const newFacet = document.getElementById(closestId);
        const selector = newFacet?.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const toFocus = newFacet?.querySelector(selector);
        const isTextInput = event.target.getAttribute('type') === 'text';
        if (toFocus && !isTextInput) toFocus.focus();
      }
    }
  }

  static renderActiveFacets(parsedDoc) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = parsedDoc.querySelector(selector);
      if (!activeFacetsElement) return;
      const host = document.querySelector(selector);
      if (host) host.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(parsedDoc) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const incoming = parsedDoc.querySelector(selector);
      const dest = document.querySelector(selector);
      if (!incoming || !dest) return;
      dest.innerHTML = incoming.innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }
// Put inside the class (static helpers)
static isSortEvent(event) {
  const t = event.target;
  if (!t) return false;
  if (t.name === 'sort_by' || t.name === 'sort_by_fake') return true;
  return Boolean(t.closest('#SortBy, #SortBy-mobile, .sort-options'));
}

static buildParamsFromForm(form) {
  const fd = new FormData(form);

  // map mobile radio -> real sort_by, then remove the fake
  const fake = fd.get('sort_by_fake');
  if (fake) {
    fd.set('sort_by', fake);
    fd.delete('sort_by_fake');
  }

  // drop empty price fields
  ['filter.v.price.gte','filter.v.price.lte'].forEach((k) => {
    const v = fd.get(k);
    if (v == null || v === '') fd.delete(k);
  });

  return new URLSearchParams(fd);
}

static mergeSortInto(baseSearchParams, sortParams) {
  const base = new URLSearchParams(baseSearchParams);
  const sortBy = sortParams.get('sort_by');
  if (sortBy != null) base.set('sort_by', sortBy);
  return FacetFiltersForm.normalizeParams(base);
}

static mergeFiltersInto(baseSearchParams, filterParams) {
  const base = new URLSearchParams(baseSearchParams);

  // remove ALL existing filters from base
  [...base.keys()].forEach((k) => {
    if (k.startsWith('filter.')) base.delete(k);
  });

  // add new filters (preserve base sort_by)
  for (const [k, v] of filterParams) {
    if (k.startsWith('filter.')) {
      base.append(k, v);               // multi-value filters ok
    } else if (k !== 'sort_by') {
      base.set(k, v);                  // q, options[prefix], etc.
    }
  }

  return FacetFiltersForm.normalizeParams(base);
}

static normalizeParams(params) {
  // ensure stable ordering: sort_by first, then others by key/value
  const out = new URLSearchParams();
  const sortVal = params.get('sort_by');
  if (sortVal != null) out.set('sort_by', sortVal);

  const rest = [];
  for (const [k, v] of params) {
    if (k === 'sort_by') continue;
    rest.push([k, v]);
  }
  rest.sort((a, b) => (a[0] === b[0] ? a[1].localeCompare(b[1]) : a[0].localeCompare(b[0])));
  rest.forEach(([k, v]) => out.append(k, v));
  return out.toString();
}

  static renderMobileCounts(source, target) {
    const targetFacetsList = target?.querySelector('.mobile-facets__list');
    const sourceFacetsList = source?.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  createSearchParams(form) {
  const fd = new FormData(form);

  // If the mobile radios were used, map them to the real param:
  const fake = fd.get('sort_by_fake');
  if (fake) {
    fd.set('sort_by', fake);   // make the real sort match the radio
  }
  fd.delete('sort_by_fake');   // never include the fake param in the URL

  // Optional: drop empty price fields so they don't clutter the URL
  ['filter.v.price.gte', 'filter.v.price.lte'].forEach((k) => {
    const v = fd.get(k);
    if (v == null || v === '') fd.delete(k);
  });

  return new URLSearchParams(fd).toString();

  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
  event.preventDefault();

  const current = window.location.search.slice(1);
  const isSort = FacetFiltersForm.isSortEvent(event);

  if (isSort) {
    // Only update sort_by, keep all existing filters
    const form = event.target.form || event.target.closest('form');
    const sortParams = FacetFiltersForm.buildParamsFromForm(form);
    const next = FacetFiltersForm.mergeSortInto(current, sortParams);
    this.onSubmitForm(next, event);
    return;
  }

  // Filters path: replace only filter.*; keep sort_by (and others) from current
  const isMobile = event.target.closest('form')?.id === 'FacetFiltersFormMobile';
  const forms = [];

  // Collect relevant filter forms (desktop vs mobile)
  document.querySelectorAll('facet-filters-form form').forEach((form) => {
    if (!isMobile) {
      if (form.id === 'FacetFiltersForm') forms.push(form);
    } else if (form.id === 'FacetFiltersFormMobile') {
      forms.push(form);
    }
  });

  // Combine params from all relevant filter forms
  const combined = new URLSearchParams();
  forms.forEach((form) => {
    const p = FacetFiltersForm.buildParamsFromForm(form);
    for (const [k, v] of p) combined.append(k, v);
  });

  const next = FacetFiltersForm.mergeFiltersInto(current, combined);
  this.onSubmitForm(next, event);
}


  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    const href = event.currentTarget?.href || '';
    const url = href.includes('?') ? href.slice(href.indexOf('?') + 1) : '';

    // --- Instantly sync the UI to the *next* query so inputs uncheck immediately ---
    FacetFiltersForm.applySearchParamsToForms(url);

    FacetFiltersForm.renderPage(url);
  }

  // \n  // Sync current forms with a given search params string (unchecks/clears removed filters)
  // \n
  static applySearchParamsToForms(searchParamsStr) {
    const params = new URLSearchParams(searchParamsStr || '');
    const forms = document.querySelectorAll('#FacetFiltersForm, #FacetFiltersFormMobile');

    forms.forEach((form) => {
      // Checkboxes
      form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        const name = input.name;
        if (!name) return;
        const values = params.getAll(name);
        input.checked = values.includes(input.value);
      });

      // Radios
      form.querySelectorAll('input[type="radio"]').forEach((input) => {
        const name = input.name;
        if (!name) return;
        const value = params.get(name);
        input.checked = value != null && value === input.value;
      });

      // Text/number inputs (e.g., price range fields)
      form.querySelectorAll('input[type="text"], input[type="number"]').forEach((input) => {
        const name = input.name;
        if (!name) return;
        const nextVal = params.get(name);
        input.value = nextVal != null ? nextVal : '';
      });

      // Selects (only set when present in params to avoid mismatches)
      form.querySelectorAll('select').forEach((select) => {
        const name = select.name;
        if (!name) return;
        const nextVal = params.get(name);
        if (nextVal != null) select.value = nextVal;
      });
    });
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.maxCachedResponses = 8;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// --- PriceRange ---
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.onRangeChange = this.onRangeChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange);
      element.addEventListener('keydown', this.onKeyDown);
    });
    this.setMinAndMaxValues();
  }

  disconnectedCallback() {
    this.querySelectorAll('input').forEach((element) => {
      element.removeEventListener('change', this.onRangeChange);
      element.removeEventListener('keydown', this.onKeyDown);
    });
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey || event.ctrlKey) return;
    const allowed = [
      'Backspace', 'Tab', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Delete', 'Escape'
    ];
    if (allowed.includes(event.key)) return;
    if (!/^[0-9.,' ]$/.test(event.key)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', minInput.getAttribute('data-max') || '');
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min') || 0);
    const max = Number(input.getAttribute('data-max') || Number.POSITIVE_INFINITY);

    if (!Number.isNaN(value)) {
      if (value < min) input.value = min;
      if (value > max) input.value = max;
    }
  }
}
customElements.define('price-range', PriceRange);

// --- FacetRemove ---
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.closeFilter = this.closeFilter.bind(this);
  }

  connectedCallback() {
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter);
    facetLink.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  disconnectedCallback() {
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.removeEventListener('click', this.closeFilter);
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form?.onActiveFilterClick(event);
  }
}
customElements.define('facet-remove', FacetRemove);
